import styled, { css } from 'styled-components'
import { desktopBreakpoint, mobileBreakpoint, tabletBreakpoint, wideScreenBreakpoint } from 'react-components'
import ChevronDownIcon from '../../../assets/icons/bold-chevron-down.svg'
import { getBasePath } from '../../../utils/miscellaneous-utils'

const BACKGROUND_IMAGE_PATH = '/assets/images/holiday-ideas/header/'

const MOBILE_IMAGE = getBasePath(`${BACKGROUND_IMAGE_PATH}/mobile.jpg?imwidth=420`)
const TABLET_IMAGE = getBasePath(`${BACKGROUND_IMAGE_PATH}/tablet.jpg?imwidth=760`)
const DESKTOP_IMAGE = getBasePath(`${BACKGROUND_IMAGE_PATH}/desktop.jpg?imwidth=1200`)

export const Wrapper = styled.section(
    ({ theme }) => css`
        background-image: url(${DESKTOP_IMAGE});
        background-position: center calc(100% - 35vh);
        background-size: cover;
        background-repeat: no-repeat;
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
        padding: ${theme.spacing.medium} ${theme.spacing.large} ${theme.spacing.xSmall};

        @media only screen and (max-width: ${tabletBreakpoint.max}px) {
            background: none;
        }

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            padding: ${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.xSmall};
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: ${theme.spacing.xLarge} ${theme.spacing.large} ${theme.spacing.medium};
        }

        @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
            padding: ${theme.spacing.xLarge} 4rem ${theme.spacing.medium};
        }

        @media (min-width: 1600px) {
            background-image: none;
            background-color: #ddfbff;
        }
    `,
)

export const Header = styled.div(
    ({ theme }) => css`
        min-height: 6.125rem;
        max-width: 75rem;
        margin-left: calc(50% - 50vw);
        margin-right: calc(50% - 50vw);
        background: url(${MOBILE_IMAGE}) center / cover no-repeat;
        padding: ${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.xSmall};

        @media (min-width: ${tabletBreakpoint.min}px) {
            background: url(${TABLET_IMAGE}) center / cover no-repeat;
            padding: ${theme.spacing.medium} ${theme.spacing.large} ${theme.spacing.xSmall};
            min-height: 6.125rem;
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            min-height: 5.25rem;
            background: none;
            margin: 0 auto;
            padding: 0;
        }
    `,
)

export const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    position: relative;
`

export const HeaderText = styled.div`
    flex: 1;
    max-width: 43.75rem;

    @media (max-width: ${mobileBreakpoint.max}px) {
        max-width: 100%;
        padding-right: 4.375rem;
    }
`

export const HeaderImg = styled.img`
    position: absolute;
    top: 10%;
    right: 1%;
    width: 4.5rem;
    height: 4.5rem;
    pointer-events: none;
`

export const ListContainer = styled.div`
    min-height: 6.125rem;
    max-width: 75rem;
    margin: 0 auto;
`

export const List = styled.div<{ expanded?: boolean }>`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    gap: 1.5rem;
    max-width: 90rem;
    margin: auto;
    will-change: contents;

    @media (min-width: ${desktopBreakpoint.min}px) {
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }

    @media (max-width: ${tabletBreakpoint.max}px) and (min-width: ${tabletBreakpoint.min}px) {
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 1rem;

        /* Show only first 10 items on tablet when not expanded */
        ${props =>
            !props.expanded &&
            `
            ${ItemLink}[data-index]:nth-child(n + 11) {
                display: none;
            }
        `}
    }

    @media (max-width: ${mobileBreakpoint.max}px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 1rem;

        /* Show only first 6 items on mobile when not expanded */
        ${props =>
            !props.expanded &&
            `
            ${ItemLink}[data-index]:nth-child(n + 7) {
                display: none;
            }
        `}
    }
`

export const ItemLink = styled.a`
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s ease;
    display: block;
    width: 100%;

    &:hover {
        transform: translateY(-0.25rem);
    }
`

export const ItemInnerContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`

export const ImageWrapper = styled.div`
    overflow: hidden;
    border-radius: 0.5rem;
    aspect-ratio: 1 / 1;
    background-color: #f5f5f5;
    img {
        transition: transform 0.3s ease;
    }

    ${ItemLink}:hover & img {
        transform: scale(1.05);
    }
`

export const Image = styled.img`
    width: 100%;
    display: block;
`

export const InfoContainer = styled.div`
    font-family: ${({ theme }) => theme.typography.font.family.lato};
    padding-top: 0.625rem;

    @media (max-width: ${mobileBreakpoint.max}px) {
        padding-top: 0.5rem;
    }
`

export const NameContainer = styled.p`
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
    color: #1a1a1a;
    margin-bottom: 0.125rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    @media (min-width: ${tabletBreakpoint.min}px) {
        font-size: 1.0625rem;
        line-height: 1.5;
    }

    @media (max-width: ${mobileBreakpoint.max}px) {
        font-size: 0.875rem;
        line-height: 1.3;
    }
`

export const Accommodation = styled.div`
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.3;
    color: #6f6f6f;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    @media (min-width: ${tabletBreakpoint.min}px) {
        font-size: 0.9375rem;
        line-height: 1.4;
    }

    @media (max-width: ${mobileBreakpoint.max}px) {
        font-size: 0.8125rem;
        line-height: 1.2;
    }
`

export const ViewMoreContainer = styled.div`
    padding: 0 1.5rem;
    margin-top: 1rem;

    @media (min-width: ${desktopBreakpoint.min}px) and (max-width: 1279px) {
        padding: 0 1rem;
    }

    @media (max-width: ${mobileBreakpoint.max}px) {
        padding: 0 1rem;
    }
`

export const ViewMoreButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    width: 11.25rem;
    min-height: 2.5rem;
    border-radius: 0.5rem;
    padding: 0.25rem 0.5rem;
    border: 1px solid #282828;
    background: transparent;
    cursor: pointer;
    text-decoration: none;
    transition: background-color 0.3s ease, transform 0.3s ease;
    margin-bottom: 1rem;

    @media (max-width: ${mobileBreakpoint.max}px) {
        width: 100%;
        gap: 0.5rem;
    }

    &:hover {
        transform: translateY(-0.125rem);
    }

    &:active {
        transform: translateY(0);
    }
`

export const ViewMoreText = styled.span`
    font-size: 0.9375rem;
    font-family: ${({ theme }) => theme.typography.font.family.lato};
    font-weight: 600;
    color: #282828;
    white-space: nowrap;

    @media (max-width: ${mobileBreakpoint.max}px) {
        font-size: 0.875rem;
    }
`

export const ArrowIcon = styled(ChevronDownIcon)<{ expanded?: boolean }>`
    margin-left: 0.25rem;
    transition: transform 0.3s ease;
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
    transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`
